import OpenInNewRounded from '@mui/icons-material/OpenInNewRounded';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Link,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { Link as RouterLink, useParams } from 'react-router';
import { AttachmentsPanel } from '../../attachments';
import { api, unwrap } from '../../../api/client';
import { useAuth } from '../../../auth/AuthProvider';
import { StatusChip } from '../../../components/StatusChip';
import { businessDate, dateTime } from '../../../lib/format';
import { useShop } from '../../../shop/ShopProvider';
import { BackLink } from '../components/ReportFrame';
import { ReportTable } from '../components/ReportTable';
import { Field, NoAccess, QueryError } from '../components/ui';
import { linkTo } from '../components/useEntityLink';
import { sourceLabel } from '../lib/accounts';
import { num } from '../lib/numbers';
import { M } from '../reports/common';

/** One journal entry with its lines; links to the source document and any reversal. */
export function JournalEntryPage() {
  const { id = '' } = useParams();
  const { can } = useAuth();
  const { format } = useShop();
  const q = useQuery({
    queryKey: ['journal-entry', id],
    queryFn: () => unwrap(api.GET('/journal-entries/{id}', { params: { path: { id } } })),
    enabled: can('journal.view'),
  });
  if (!can('journal.view'))
    return (
      <>
        <BackLink to="/accounting?tab=journal" label="Journal" />
        <NoAccess what="journal entries" />
      </>
    );
  const e = q.data;
  const debit = e?.lines.reduce((s, l) => s + num(l.debit), 0) ?? 0;
  const credit = e?.lines.reduce((s, l) => s + num(l.credit), 0) ?? 0;
  const source = e ? linkTo(e.sourceType, e.sourceId) : null;
  return (
    <>
      <BackLink to="/accounting?tab=journal" label="Journal" />
      {q.error && <QueryError error={q.error} onRetry={() => void q.refetch()} />}
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
        sx={{ justifyContent: 'space-between', alignItems: { sm: 'flex-end' }, mb: 2.5 }}
      >
        <Box>
          <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center' }}>
            <Typography variant="h3">
              {e ? (e.memo ?? sourceLabel(e.sourceType)) : <Skeleton width={260} />}
            </Typography>
            {e && (
              <StatusChip
                status={e.reversedById ? 'VOID' : 'POSTED'}
                label={e.reversedById ? 'Reversed' : e.reversalOfId ? 'Reversal' : 'Posted'}
              />
            )}
          </Stack>
          <Typography color="text.secondary" sx={{ mt: 0.5 }}>
            Journal entry {e ? `· ${businessDate(e.entryDate, format)}` : ''}
          </Typography>
        </Box>
        {source && (
          <Button
            component={RouterLink}
            to={source}
            variant="outlined"
            endIcon={<OpenInNewRounded />}
          >
            Open {e && sourceLabel(e.sourceType).toLowerCase()}
          </Button>
        )}
      </Stack>
      {e?.reversedById && (
        <Alert severity="warning" sx={{ mb: 2.5 }}>
          This entry was reversed.{' '}
          <Link component={RouterLink} to={`/accounting/journal/${e.reversedById}`}>
            View the reversing entry
          </Link>
        </Alert>
      )}
      {e?.reversalOfId && (
        <Alert severity="info" sx={{ mb: 2.5 }}>
          This entry reverses{' '}
          <Link component={RouterLink} to={`/accounting/journal/${e.reversalOfId}`}>
            an earlier entry
          </Link>
          .
        </Alert>
      )}
      <Card sx={{ mb: 2.5 }}>
        <CardContent>
          <Box
            sx={{
              display: 'grid',
              gap: 2.5,
              gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' },
            }}
          >
            <Field label="Entry date">{e ? businessDate(e.entryDate, format) : '…'}</Field>
            <Field label="Posted">{e ? dateTime(e.postedAt, format) : '…'}</Field>
            <Field label="Source">{e ? sourceLabel(e.sourceType) : '…'}</Field>
            <Field label="Balanced">
              {e ? (Math.abs(debit - credit) < 0.0001 ? 'Yes — debits equal credits' : 'No') : '…'}
            </Field>
          </Box>
        </CardContent>
      </Card>
      <ReportTable
        loading={q.isLoading}
        rows={e?.lines}
        rowKey={(l) => String(l.lineNo)}
        dense
        columns={[
          {
            key: 'account',
            header: 'Account',
            render: (l) => (
              <Box>
                <Link
                  component={RouterLink}
                  to={`/accounting/accounts/${l.accountId}`}
                  underline="hover"
                  sx={{ fontWeight: 600 }}
                >
                  {l.accountCode} · {l.accountName}
                </Link>
                {l.memo && (
                  <Typography variant="caption" color="text.secondary" component="div">
                    {l.memo}
                  </Typography>
                )}
              </Box>
            ),
          },
          {
            key: 'debit',
            header: 'Debit',
            align: 'right',
            render: (l) => (num(l.debit) ? <M v={l.debit} /> : ''),
            footer: <M v={debit} />,
          },
          {
            key: 'credit',
            header: 'Credit',
            align: 'right',
            render: (l) => (num(l.credit) ? <M v={l.credit} /> : ''),
            footer: <M v={credit} />,
          },
        ]}
      />
      <AttachmentsPanel recordType="JOURNAL_ENTRY" recordId={id} sx={{ mt: 3 }} />
    </>
  );
}
